import { useCookieContext } from 'context';
import parse from 'html-react-parser';
import React, { useEffect, useState } from 'react';

import './AgentExecution.scss';
import { useTypewriter } from '../useTypewriter';
import { AgentMessageFeedback } from './AgentMessageFeedback';
import { getGatewayURL } from '../../../utils/apiUtils';

import { useMessagesContext } from 'components/chat/MessagesContext';

interface AgentExecutionProps {
  userInput: string;
  isUser: boolean;
  messageId: number;
  onContentUpdate: () => void;
}

type Reference = {
  title: string;
  url: string;
};

const GATEWAY_URL = getGatewayURL();

const AgentExecution = ({
  userInput,
  isUser,
  messageId,
  onContentUpdate,
}: AgentExecutionProps): React.ReactElement => {
  const [finalResponse, setFinalResponse] = useState<string | null>(null);
  const [references, setReferences] = useState<Reference[]>([]);
  const [waitingForFirstEvent, setWaitingForFirstEvent] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { cookie } = useCookieContext();
  // Use the typewriter effect for the final response
  const [displayedFinalResponse, isDone] = useTypewriter(finalResponse ?? '');
  const { setAgentExecuting, addResponseToAgentMessage } = useMessagesContext();

  useEffect(() => {
    // Scroll to bottom whenever content updates
    if (onContentUpdate) {
      onContentUpdate();
    }
  }, [displayedFinalResponse, waitingForFirstEvent, error]);

  useEffect(() => {
    let isMounted = true;

    // Function to process each invocation as it comes in
    const processInvocation = (invocation: string) => {
      const parsedInvocation = JSON.parse(invocation);

      // Remove the initial spinning wheel once we receive the first event
      if (waitingForFirstEvent) {
        setWaitingForFirstEvent(false);
      }

      // Support bot response
      if (parsedInvocation.success.body.output && isMounted) {
        const finalResponse = parsedInvocation.success.body.output;
        const references = parsedInvocation.success.body.refs || [];
        setReferences(references);
        setFinalResponse(finalResponse);
        addResponseToAgentMessage(finalResponse, messageId);
      }
    };

    // Function to connect to the backend and process the streaming response
    const fetchAgentStream = async () => {
      setAgentExecuting(true);
      try {
        const bearer = cookie || '';

        const response = await fetch(`${GATEWAY_URL}/support-agent/api/v1/supportDocBotStream`, {
          method: 'POST',
          // credentials: 'include',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Authorization: bearer,
            'X-Request-Id': crypto.randomUUID(),
          },
          // Inside fetchAgentStream function
          body: JSON.stringify({
            query: userInput,
          }),
        });

        if (!response.body) {
          setError('ReadableStream not supported in this browser.');
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let { value, done } = await reader.read();

        while (!done && isMounted) {
          const chunk = decoder.decode(value, { stream: true });
          // Split the chunk into individual JSON strings using newline as the delimiter
          const invocations = chunk.trim().split(/\r?\n/);
          // Process each invocation separately
          invocations.forEach((invocation) => {
            processInvocation(invocation);
          });
          // Read the next chunk
          ({ value, done } = await reader.read());
        }
      } catch (error) {
        setError('Error fetching agent stream.');
      } finally {
        setAgentExecuting(false);
      }
    };

    fetchAgentStream();

    return () => {
      isMounted = false;
    };
  }, [userInput]);

  return (
    <>
      {error ? (
        <div className='error-alert'>
          <p>{error}</p>
        </div>
      ) : (
        <div className='agent-response'>
          <div className={`message-bubble ${isUser ? 'user-message' : 'system-message'}`}>
            {/* Show the initial spinning wheel while waiting for the first event */}
            {waitingForFirstEvent && (
              <div data-testid={'agent-current-action'} className='agent-current-action'>
                <span data-testid={'spinner'} className='spinner'></span> Waiting for the
                response...
              </div>
            )}

            {/* Display the final response with the typewriter effect if available */}
            {finalResponse && (
              <div data-testid={'agent-final-response'} className='agent-final-response'>
                <p>
                  {typeof displayedFinalResponse === 'string'
                    ? parse(displayedFinalResponse)
                    : `Sorry! I am not sure. Please try again!`}
                </p>
                {isDone && references.length > 0 && (
                  <ul data-testid={'reference-list'} id='ref-list-id'>
                    {references.map((reference: Reference, index: number) => (
                      <li data-testid={`reference-link-${index}`} key={index}>
                        <a href={reference.url} target='_blank' rel='noreferrer'>
                          {reference.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          {finalResponse && <AgentMessageFeedback messageId={messageId} />}
        </div>
      )}
    </>
  );
};

export default AgentExecution;
